import { useQueryClient } from '@tanstack/react-query';
import { createParticipantExperienceTileItem } from '@/components/shared';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { useParams } from 'react-router-dom';
import {
  BackofficePageModeActionMenu,
  CustomizableDetailTabs,
  PageHeader,
  panelSpanFromLayout,
  SingleValuePanel,
  type TableActionItem,
  useCustomizableDetailTabs,
  useBackofficePageMode,
  TagsTile,
} from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import {
  fetchBackofficeProjectDetail,
  fetchBackofficeProjectInterviews,
  fetchBackofficeProjectTargetScope,
} from './api';
import {
  ChangeProjectOwnershipModal,
  ChangeProjectProductLineModal,
  ProjectProductLinePanel,
  ProjectInterviewLinkPanel,
  EditProjectParticipantPrivacyModal,
  ProjectAccessPanel,
  ProjectDeletedChip,
  ProjectDetailPanel,
  ProjectDetailPanelGrid,
  ProjectDetailsPanel,
  ProjectInterviewsListSection,
  ProjectParticipantPrivacyPanel,
  ProjectTargetScopeCreatePanel,
  ProjectTargetScopeDesignPanel,
  ProjectTargetScopeRecruitPanel,
  ProjectTargetScopeSummaryPanel,
  ProjectStatusChip,
  type ProjectDetailsTab,
} from './components';
import { formatRelativeTimeFromNow } from './format';
import type {
  BackofficeProjectDetail,
  BackofficeProjectInterviewsResponse,
  BackofficeProjectTargetScope,
} from './types';

const PROJECT_BUILT_IN_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'target-scope', label: 'Target and scope' },
  { id: 'interviews', label: 'Interviews' },
] satisfies Array<{ id: ProjectDetailsTab; label: string }>;

const STUDY_DETAILS_TILE_DESCRIPTION =
  'Study metadata, privacy settings, owner, organization, language, and creation details.';

export function BackofficeProjectDetailsPage() {
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<BackofficeProjectDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [targetScope, setTargetScope] = useState<BackofficeProjectTargetScope | null>(null);
  const [targetScopeError, setTargetScopeError] = useState<string | null>(null);
  const [targetScopeLoading, setTargetScopeLoading] = useState(false);
  const [changeProductLineOpen, setChangeProductLineOpen] = useState(false);
  const [changeOwnershipOpen, setChangeOwnershipOpen] = useState(false);
  const [editParticipantPrivacyOpen, setEditParticipantPrivacyOpen] = useState(false);
  const { pageMode, setPageMode } = useBackofficePageMode();
  const detailBasePath = projectId ? `/projects/${projectId}` : '/projects';
  const detailTabs = useCustomizableDetailTabs<ProjectDetailsTab>({
    preferenceScope: 'details:project:tabs',
    builtInTabs: PROJECT_BUILT_IN_TABS,
    defaultTabId: 'overview',
    basePath: detailBasePath,
    pageMode,
  });
  const activeTab = detailTabs.activeBuiltInTabId;

  useBackofficePageTitle(project ? `Study: ${project.title}` : 'Study');

  const loadProjectInterviews = useCallback(
    async (
      params: URLSearchParams,
      signal: AbortSignal
    ): Promise<Pick<BackofficeProjectInterviewsResponse, 'interviews' | 'page'>> => {
      if (!projectId) {
        throw new Error('Study not found');
      }

      return fetchBackofficeProjectInterviews(projectId, params, signal);
    },
    [projectId]
  );

  useEffect(() => {
    if (!projectId) {
      setProject(null);
      setError('Study not found');
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchBackofficeProjectDetail(projectId, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) {
          setProject(response.project);
        }
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setProject(null);
        setError(caught instanceof Error ? caught.message : 'Failed to load study');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const controller = new AbortController();
    setTargetScopeLoading(true);
    setTargetScopeError(null);

    fetchBackofficeProjectTargetScope(projectId, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) {
          setTargetScope(response.targetScope);
        }
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setTargetScope(null);
        setTargetScopeError(
          caught instanceof Error ? caught.message : 'Failed to load target and scope'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setTargetScopeLoading(false);
        }
      });

    return () => controller.abort();
  }, [projectId]);

  const renderTargetScopeTileContent = (
    renderContent: (loadedTargetScope: BackofficeProjectTargetScope) => ReactNode
  ) => {
    if (targetScopeLoading) {
      return (
        <div className="px-5 py-16 text-center text-body text-neutral-500">
          Loading target and scope
        </div>
      );
    }

    if (targetScopeError) {
      return (
        <div className="px-5 py-16 text-center text-body text-red-700">{targetScopeError}</div>
      );
    }

    return targetScope ? renderContent(targetScope) : null;
  };

  const createInterviewLinkTileItem = (project: BackofficeProjectDetail) => ({
    id: 'study-interview-link',
    title: 'Interview link',
    description: 'Participant interview URL with settings to choose an available hostname.',
    layout: { x: 1, y: 24, w: 2, h: 1, minW: 1, maxW: 4, minH: 1, maxH: 2, isResizable: true },
    render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
      <ProjectInterviewLinkPanel
        project={project}
        currentSpan={panelSpanFromLayout(layout, '2x1')}
        onUpdated={() => {
          if (projectId)
            void fetchBackofficeProjectDetail(projectId)
              .then((response) => setProject(response.project))
              .catch(() => undefined);
        }}
      />
    ),
  });

  const createProductLineTileItem = (project: BackofficeProjectDetail) => ({
    id: 'study-product-line',
    title: "Study's product line",
    description: 'Current product-line assignment with settings to change it.',
    layout: { x: 0, y: 24, w: 1, h: 1, isResizable: false },
    render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
      <ProjectProductLinePanel
        project={project}
        currentSpan={panelSpanFromLayout(layout, '1x1')}
        onEdit={() => setChangeProductLineOpen(true)}
      />
    ),
  });

  const createTargetScopeTileItems = (startY: number) => [
    {
      id: 'target-scope-summary',
      title: 'Target and scope summary',
      description: 'Study status, organization, interview limit, and interview counts.',
      layout: {
        x: 0,
        y: startY,
        w: 2,
        h: 2,
        minW: 1,
        minH: 1,
        maxW: 4,
        maxH: 3,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <ProjectDetailPanel
          title="Target and scope summary"
          span="2x2"
          panelId="target-scope-summary"
          currentSpan={panelSpanFromLayout(layout, '2x2')}
        >
          {renderTargetScopeTileContent((loadedTargetScope) => (
            <ProjectTargetScopeSummaryPanel targetScope={loadedTargetScope} />
          ))}
        </ProjectDetailPanel>
      ),
    },
    {
      id: 'target-scope-create',
      title: 'Research setup',
      description: 'Objective, target group, interview target, and drafted questions.',
      layout: {
        x: 0,
        y: startY + 2,
        w: 4,
        h: 4,
        minW: 2,
        minH: 2,
        maxW: 6,
        maxH: 5,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <ProjectDetailPanel
          title="Research setup"
          span="4x4"
          panelId="target-scope-create"
          currentSpan={panelSpanFromLayout(layout, '4x4')}
        >
          {renderTargetScopeTileContent((loadedTargetScope) => (
            <ProjectTargetScopeCreatePanel targetScope={loadedTargetScope} />
          ))}
        </ProjectDetailPanel>
      ),
    },
    {
      id: 'target-scope-design',
      title: 'Interview design',
      description: 'Participant copy, moderators, interview settings, and brand setup.',
      layout: {
        x: 0,
        y: startY + 6,
        w: 4,
        h: 4,
        minW: 2,
        minH: 2,
        maxW: 6,
        maxH: 5,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <ProjectDetailPanel
          title="Interview design"
          span="4x4"
          panelId="target-scope-design"
          currentSpan={panelSpanFromLayout(layout, '4x4')}
        >
          {renderTargetScopeTileContent((loadedTargetScope) => (
            <ProjectTargetScopeDesignPanel targetScope={loadedTargetScope} />
          ))}
        </ProjectDetailPanel>
      ),
    },
    {
      id: 'target-scope-recruit',
      title: 'Recruitment setup',
      description: 'Panel orders, participant imports, segments, and assignment status.',
      layout: {
        x: 0,
        y: startY + 10,
        w: 4,
        h: 4,
        minW: 2,
        minH: 2,
        maxW: 6,
        maxH: 5,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <ProjectDetailPanel
          title="Recruitment setup"
          span="4x4"
          panelId="target-scope-recruit"
          currentSpan={panelSpanFromLayout(layout, '4x4')}
        >
          {renderTargetScopeTileContent((loadedTargetScope) => (
            <ProjectTargetScopeRecruitPanel targetScope={loadedTargetScope} />
          ))}
        </ProjectDetailPanel>
      ),
    },
  ];

  const createProjectTagsTileItem = (loadedProject: BackofficeProjectDetail, y: number) => ({
    id: 'tags',
    title: 'Tags',
    description: 'Tags assigned to this study.',
    layout: {
      x: 0,
      y,
      w: 2,
      h: 1,
      minW: 1,
      minH: 1,
      maxW: 4,
      maxH: 3,
      isResizable: true,
    },
    render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
      <TagsTile
        entityType="project"
        entityId={loadedProject.id}
        panelId="tags"
        currentSpan={panelSpanFromLayout(layout, '2x1')}
      />
    ),
  });

  return (
    <main className="min-h-[320px] bg-neutral-50 p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        <PageHeader
          adornment={
            project ? (
              <>
                <ProjectStatusChip status={project.status} />
                {project.deletedAt ? <ProjectDeletedChip /> : null}
              </>
            ) : null
          }
          actions={
            project && !project.deletedAt ? (
              <BackofficePageModeActionMenu
                pageMode={pageMode}
                onPageModeChange={setPageMode}
                label="Study actions"
                viewActions={[
                  {
                    id: 'change-owner-organization',
                    label: 'Change owner/organization',
                    icon: ArrowLeftRight,
                    onSelect: () => setChangeOwnershipOpen(true),
                  } satisfies TableActionItem,
                ]}
              />
            ) : null
          }
          showShareButton={pageMode !== 'edit'}
        >
          {project?.title ?? 'Study'}
        </PageHeader>

        <CustomizableDetailTabs tabs={detailTabs} />

        {loading ? (
          <div className="rounded-lg border border-neutral-200 bg-white px-5 py-16 text-center text-body text-neutral-500 shadow-sm">
            Loading study
          </div>
        ) : error ? (
          <div className="rounded-lg border border-neutral-200 bg-white px-5 py-16 text-center text-body text-red-700 shadow-sm">
            {error}
          </div>
        ) : project && activeTab === 'overview' ? (
          <>
            <ProjectDetailPanelGrid
              resetKey={project.id}
              preferenceScope="details:project:overview"
              layoutMode={pageMode}
              defaultVisibleItemIds={[
                'participant-experience',
                'project-details',
                'participant-privacy',
                'interviews',
                'lifecycle',
                'last-interview',
                'study-access',
              ]}
              tileCatalogLabel="Add study tile"
              items={[
                createProductLineTileItem(project),
                createInterviewLinkTileItem(project),
                createParticipantExperienceTileItem({
                  organizationId: project.organizationId,
                  study: project.id,
                  disabled: !!project.deletedAt,
                }),
                {
                  id: 'project-details',
                  title: 'Study details',
                  description: STUDY_DETAILS_TILE_DESCRIPTION,
                  layout: {
                    x: 0,
                    y: 0,
                    w: 2,
                    h: 4,
                    minW: 1,
                    minH: 1,
                    maxW: 4,
                    maxH: 4,
                    isResizable: true,
                  },
                  render: (layout) => (
                    <ProjectDetailsPanel
                      project={project}
                      panelId="project-details"
                      currentSpan={panelSpanFromLayout(layout, '2x4')}
                    />
                  ),
                },
                {
                  id: 'interviews',
                  title: 'Interviews',
                  description: 'Current interview count with a shortcut to the interviews tab.',
                  layout: { x: 2, y: 0, w: 1, h: 1, isResizable: false },
                  render: (layout) => (
                    <SingleValuePanel
                      panelId="interviews"
                      currentSpan={panelSpanFromLayout(layout, '1x1')}
                      title="Interviews"
                      value={String(project.interviewCount)}
                      footer={project.interviewCount === 1 ? 'interview' : 'interviews'}
                      linkTo={detailTabs.getTabHref('interviews')}
                      linkLabel={`View interviews for ${project.title}`}
                    />
                  ),
                },
                {
                  id: 'lifecycle',
                  title: project.lifecycleMetric.label,
                  description: 'Most relevant lifecycle timestamp for the current study state.',
                  layout: { x: 3, y: 0, w: 1, h: 1, isResizable: false },
                  render: (layout) => (
                    <SingleValuePanel
                      panelId="lifecycle"
                      currentSpan={panelSpanFromLayout(layout, '1x1')}
                      title={project.lifecycleMetric.label}
                      value={formatRelativeTimeFromNow(project.lifecycleMetric.occurredAt).value}
                      footer={
                        formatRelativeTimeFromNow(project.lifecycleMetric.occurredAt).suffix ??
                        undefined
                      }
                    />
                  ),
                },
                {
                  id: 'last-interview',
                  title: 'Last interview',
                  description: 'Relative time since the latest interview was created.',
                  layout: { x: 2, y: 1, w: 1, h: 1, isResizable: false },
                  render: (layout) => (
                    <SingleValuePanel
                      panelId="last-interview"
                      currentSpan={panelSpanFromLayout(layout, '1x1')}
                      title="Last interview"
                      value={formatRelativeTimeFromNow(project.lastInterviewAt).value}
                      footer={
                        formatRelativeTimeFromNow(project.lastInterviewAt).suffix ?? undefined
                      }
                    />
                  ),
                },
                {
                  id: 'participant-privacy',
                  title: 'Participant privacy',
                  description: 'Current study privacy settings with quick edit access.',
                  layout: {
                    x: 2,
                    y: 2,
                    w: 2,
                    h: 1,
                    minW: 1,
                    minH: 1,
                    maxW: 4,
                    maxH: 2,
                    isResizable: true,
                  },
                  render: (layout) => (
                    <ProjectParticipantPrivacyPanel
                      project={project}
                      panelId="participant-privacy"
                      currentSpan={panelSpanFromLayout(layout, '2x1')}
                      onEdit={() => setEditParticipantPrivacyOpen(true)}
                    />
                  ),
                },
                {
                  id: 'study-access',
                  title: 'Study access',
                  description: 'Users with explicit access to this study.',
                  layout: {
                    x: 2,
                    y: 3,
                    w: 2,
                    h: 2,
                    minW: 1,
                    minH: 1,
                    maxW: 4,
                    maxH: 4,
                    isResizable: true,
                  },
                  render: (layout) => (
                    <ProjectAccessPanel
                      accessUsers={project.accessUsers}
                      panelId="study-access"
                      currentSpan={panelSpanFromLayout(layout, '2x2')}
                    />
                  ),
                },
                ...createTargetScopeTileItems(5),
                {
                  id: 'interviews-table',
                  title: 'Interviews table',
                  description: 'Filter, sort, and open interviews for this study.',
                  layout: {
                    x: 0,
                    y: 19,
                    w: 4,
                    h: 4,
                    minW: 2,
                    minH: 2,
                    maxW: 6,
                    maxH: 5,
                    isResizable: true,
                  },
                  render: (layout) => (
                    <ProjectDetailPanel
                      title="Interviews"
                      span="4x4"
                      panelId="interviews-table"
                      currentSpan={panelSpanFromLayout(layout, '4x4')}
                      contentClassName="-mx-5 -mb-5"
                      contentOverflow="hidden"
                    >
                      <ProjectInterviewsListSection
                        contextLabel={project.title}
                        preferenceScope={`list:project:${project.id}:interviews`}
                        surface="tile"
                        loadPage={loadProjectInterviews}
                      />
                    </ProjectDetailPanel>
                  ),
                },
                createProjectTagsTileItem(project, 23),
              ]}
            />
          </>
        ) : project && detailTabs.activeCustomTab ? (
          <ProjectDetailPanelGrid
            resetKey={detailTabs.activeCustomTab.id}
            preferenceScope={`details:project:${detailTabs.activeCustomTab.id}`}
            layoutMode={pageMode}
            defaultVisibleItemIds={[]}
            tileCatalogLabel="Add study tile"
            items={[
              createProductLineTileItem(project),
              createInterviewLinkTileItem(project),
              createParticipantExperienceTileItem({
                organizationId: project.organizationId,
                study: project.id,
                disabled: !!project.deletedAt,
              }),
              {
                id: 'project-details',
                title: 'Study details',
                description: STUDY_DETAILS_TILE_DESCRIPTION,
                layout: {
                  x: 0,
                  y: 0,
                  w: 2,
                  h: 4,
                  minW: 1,
                  minH: 1,
                  maxW: 4,
                  maxH: 4,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectDetailsPanel
                    project={project}
                    panelId="project-details"
                    currentSpan={panelSpanFromLayout(layout, '2x4')}
                  />
                ),
              },
              {
                id: 'interviews',
                title: 'Interviews',
                description: 'Current interview count with a shortcut to the interviews tab.',
                layout: { x: 2, y: 0, w: 1, h: 1, isResizable: false },
                render: (layout) => (
                  <SingleValuePanel
                    panelId="interviews"
                    currentSpan={panelSpanFromLayout(layout, '1x1')}
                    title="Interviews"
                    value={String(project.interviewCount)}
                    footer={project.interviewCount === 1 ? 'interview' : 'interviews'}
                    linkTo={detailTabs.getTabHref('interviews')}
                    linkLabel={`View interviews for ${project.title}`}
                  />
                ),
              },
              {
                id: 'lifecycle',
                title: project.lifecycleMetric.label,
                description: 'Most relevant lifecycle timestamp for the current study state.',
                layout: { x: 3, y: 0, w: 1, h: 1, isResizable: false },
                render: (layout) => (
                  <SingleValuePanel
                    panelId="lifecycle"
                    currentSpan={panelSpanFromLayout(layout, '1x1')}
                    title={project.lifecycleMetric.label}
                    value={formatRelativeTimeFromNow(project.lifecycleMetric.occurredAt).value}
                    footer={
                      formatRelativeTimeFromNow(project.lifecycleMetric.occurredAt).suffix ??
                      undefined
                    }
                  />
                ),
              },
              {
                id: 'last-interview',
                title: 'Last interview',
                description: 'Relative time since the latest interview was created.',
                layout: { x: 2, y: 1, w: 1, h: 1, isResizable: false },
                render: (layout) => (
                  <SingleValuePanel
                    panelId="last-interview"
                    currentSpan={panelSpanFromLayout(layout, '1x1')}
                    title="Last interview"
                    value={formatRelativeTimeFromNow(project.lastInterviewAt).value}
                    footer={formatRelativeTimeFromNow(project.lastInterviewAt).suffix ?? undefined}
                  />
                ),
              },
              {
                id: 'participant-privacy',
                title: 'Participant privacy',
                description: 'Current study privacy settings with quick edit access.',
                layout: {
                  x: 2,
                  y: 2,
                  w: 2,
                  h: 1,
                  minW: 1,
                  minH: 1,
                  maxW: 4,
                  maxH: 2,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectParticipantPrivacyPanel
                    project={project}
                    panelId="participant-privacy"
                    currentSpan={panelSpanFromLayout(layout, '2x1')}
                    onEdit={() => setEditParticipantPrivacyOpen(true)}
                  />
                ),
              },
              {
                id: 'study-access',
                title: 'Study access',
                description: 'Users with explicit access to this study.',
                layout: {
                  x: 2,
                  y: 3,
                  w: 2,
                  h: 2,
                  minW: 1,
                  minH: 1,
                  maxW: 4,
                  maxH: 4,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectAccessPanel
                    accessUsers={project.accessUsers}
                    panelId="study-access"
                    currentSpan={panelSpanFromLayout(layout, '2x2')}
                  />
                ),
              },
              ...createTargetScopeTileItems(5),
              {
                id: 'interviews-table',
                title: 'Interviews table',
                description: 'Filter, sort, and open interviews for this study.',
                layout: {
                  x: 0,
                  y: 19,
                  w: 4,
                  h: 4,
                  minW: 2,
                  minH: 2,
                  maxW: 6,
                  maxH: 5,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectDetailPanel
                    title="Interviews"
                    span="4x4"
                    panelId="interviews-table"
                    currentSpan={panelSpanFromLayout(layout, '4x4')}
                    contentClassName="-mx-5 -mb-5"
                    contentOverflow="hidden"
                  >
                    <ProjectInterviewsListSection
                      contextLabel={project.title}
                      preferenceScope={`list:project:${project.id}:interviews`}
                      surface="tile"
                      loadPage={loadProjectInterviews}
                    />
                  </ProjectDetailPanel>
                ),
              },
              createProjectTagsTileItem(project, 23),
            ]}
          />
        ) : project && activeTab === 'target-scope' ? (
          <ProjectDetailPanelGrid
            resetKey={project.id}
            preferenceScope="details:project:target-scope:v2"
            layoutMode={pageMode}
            defaultVisibleItemIds={[
              'target-scope-summary',
              'target-scope-create',
              'target-scope-design',
              'target-scope-recruit',
              'participant-privacy',
            ]}
            tileCatalogLabel="Add study tile"
            items={[
              createProductLineTileItem(project),
              createInterviewLinkTileItem(project),
              createParticipantExperienceTileItem({
                organizationId: project.organizationId,
                study: project.id,
                disabled: !!project.deletedAt,
              }),
              ...createTargetScopeTileItems(0),
              {
                id: 'project-details',
                title: 'Study details',
                description: STUDY_DETAILS_TILE_DESCRIPTION,
                layout: {
                  x: 0,
                  y: 14,
                  w: 2,
                  h: 4,
                  minW: 1,
                  minH: 1,
                  maxW: 4,
                  maxH: 4,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectDetailsPanel
                    project={project}
                    panelId="project-details"
                    currentSpan={panelSpanFromLayout(layout, '2x4')}
                  />
                ),
              },
              {
                id: 'interviews',
                title: 'Interviews',
                description: 'Current interview count with a shortcut to the interviews tab.',
                layout: { x: 2, y: 14, w: 1, h: 1, isResizable: false },
                render: (layout) => (
                  <SingleValuePanel
                    panelId="interviews"
                    currentSpan={panelSpanFromLayout(layout, '1x1')}
                    title="Interviews"
                    value={String(project.interviewCount)}
                    footer={project.interviewCount === 1 ? 'interview' : 'interviews'}
                    linkTo={detailTabs.getTabHref('interviews')}
                    linkLabel={`View interviews for ${project.title}`}
                  />
                ),
              },
              {
                id: 'lifecycle',
                title: project.lifecycleMetric.label,
                description: 'Most relevant lifecycle timestamp for the current study state.',
                layout: { x: 3, y: 14, w: 1, h: 1, isResizable: false },
                render: (layout) => (
                  <SingleValuePanel
                    panelId="lifecycle"
                    currentSpan={panelSpanFromLayout(layout, '1x1')}
                    title={project.lifecycleMetric.label}
                    value={formatRelativeTimeFromNow(project.lifecycleMetric.occurredAt).value}
                    footer={
                      formatRelativeTimeFromNow(project.lifecycleMetric.occurredAt).suffix ??
                      undefined
                    }
                  />
                ),
              },
              {
                id: 'last-interview',
                title: 'Last interview',
                description: 'Relative time since the latest interview was created.',
                layout: { x: 2, y: 15, w: 1, h: 1, isResizable: false },
                render: (layout) => (
                  <SingleValuePanel
                    panelId="last-interview"
                    currentSpan={panelSpanFromLayout(layout, '1x1')}
                    title="Last interview"
                    value={formatRelativeTimeFromNow(project.lastInterviewAt).value}
                    footer={formatRelativeTimeFromNow(project.lastInterviewAt).suffix ?? undefined}
                  />
                ),
              },
              {
                id: 'participant-privacy',
                title: 'Participant privacy',
                description: 'Current study privacy settings with quick edit access.',
                layout: {
                  x: 2,
                  y: 16,
                  w: 2,
                  h: 1,
                  minW: 1,
                  minH: 1,
                  maxW: 4,
                  maxH: 2,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectParticipantPrivacyPanel
                    project={project}
                    panelId="participant-privacy"
                    currentSpan={panelSpanFromLayout(layout, '2x1')}
                    onEdit={() => setEditParticipantPrivacyOpen(true)}
                  />
                ),
              },
              {
                id: 'study-access',
                title: 'Study access',
                description: 'Users with explicit access to this study.',
                layout: {
                  x: 2,
                  y: 17,
                  w: 2,
                  h: 2,
                  minW: 1,
                  minH: 1,
                  maxW: 4,
                  maxH: 4,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectAccessPanel
                    accessUsers={project.accessUsers}
                    panelId="study-access"
                    currentSpan={panelSpanFromLayout(layout, '2x2')}
                  />
                ),
              },
              {
                id: 'interviews-table',
                title: 'Interviews table',
                description: 'Filter, sort, and open interviews for this study.',
                layout: {
                  x: 0,
                  y: 19,
                  w: 4,
                  h: 4,
                  minW: 2,
                  minH: 2,
                  maxW: 6,
                  maxH: 5,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectDetailPanel
                    title="Interviews"
                    span="4x4"
                    panelId="interviews-table"
                    currentSpan={panelSpanFromLayout(layout, '4x4')}
                    contentClassName="-mx-5 -mb-5"
                    contentOverflow="hidden"
                  >
                    <ProjectInterviewsListSection
                      contextLabel={project.title}
                      preferenceScope={`list:project:${project.id}:interviews`}
                      surface="tile"
                      loadPage={loadProjectInterviews}
                    />
                  </ProjectDetailPanel>
                ),
              },
              createProjectTagsTileItem(project, 23),
            ]}
          />
        ) : project && activeTab === 'interviews' ? (
          <ProjectDetailPanelGrid
            resetKey={project.id}
            preferenceScope="details:project:interviews"
            layoutMode={pageMode}
            defaultVisibleItemIds={['interviews-table', 'participant-privacy']}
            tileCatalogLabel="Add study tile"
            items={[
              createProductLineTileItem(project),
              createInterviewLinkTileItem(project),
              createParticipantExperienceTileItem({
                organizationId: project.organizationId,
                study: project.id,
                disabled: !!project.deletedAt,
              }),
              {
                id: 'interviews-table',
                title: 'Interviews table',
                description: 'Filter, sort, and open interviews for this study.',
                layout: {
                  x: 0,
                  y: 0,
                  w: 4,
                  h: 4,
                  minW: 2,
                  minH: 2,
                  maxW: 6,
                  maxH: 5,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectDetailPanel
                    title="Interviews"
                    span="4x4"
                    panelId="interviews-table"
                    currentSpan={panelSpanFromLayout(layout, '4x4')}
                    contentClassName="-mx-5 -mb-5"
                    contentOverflow="hidden"
                  >
                    <ProjectInterviewsListSection
                      contextLabel={project.title}
                      preferenceScope={`list:project:${project.id}:interviews`}
                      surface="tile"
                      loadPage={loadProjectInterviews}
                    />
                  </ProjectDetailPanel>
                ),
              },
              {
                id: 'project-details',
                title: 'Study details',
                description: STUDY_DETAILS_TILE_DESCRIPTION,
                layout: {
                  x: 0,
                  y: 4,
                  w: 2,
                  h: 4,
                  minW: 1,
                  minH: 1,
                  maxW: 4,
                  maxH: 4,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectDetailsPanel
                    project={project}
                    panelId="project-details"
                    currentSpan={panelSpanFromLayout(layout, '2x4')}
                  />
                ),
              },
              {
                id: 'interviews',
                title: 'Interviews',
                description: 'Current interview count with a shortcut to the interviews tab.',
                layout: { x: 2, y: 4, w: 1, h: 1, isResizable: false },
                render: (layout) => (
                  <SingleValuePanel
                    panelId="interviews"
                    currentSpan={panelSpanFromLayout(layout, '1x1')}
                    title="Interviews"
                    value={String(project.interviewCount)}
                    footer={project.interviewCount === 1 ? 'interview' : 'interviews'}
                    linkTo={detailTabs.getTabHref('interviews')}
                    linkLabel={`View interviews for ${project.title}`}
                  />
                ),
              },
              {
                id: 'lifecycle',
                title: project.lifecycleMetric.label,
                description: 'Most relevant lifecycle timestamp for the current study state.',
                layout: { x: 3, y: 4, w: 1, h: 1, isResizable: false },
                render: (layout) => (
                  <SingleValuePanel
                    panelId="lifecycle"
                    currentSpan={panelSpanFromLayout(layout, '1x1')}
                    title={project.lifecycleMetric.label}
                    value={formatRelativeTimeFromNow(project.lifecycleMetric.occurredAt).value}
                    footer={
                      formatRelativeTimeFromNow(project.lifecycleMetric.occurredAt).suffix ??
                      undefined
                    }
                  />
                ),
              },
              {
                id: 'last-interview',
                title: 'Last interview',
                description: 'Relative time since the latest interview was created.',
                layout: { x: 2, y: 5, w: 1, h: 1, isResizable: false },
                render: (layout) => (
                  <SingleValuePanel
                    panelId="last-interview"
                    currentSpan={panelSpanFromLayout(layout, '1x1')}
                    title="Last interview"
                    value={formatRelativeTimeFromNow(project.lastInterviewAt).value}
                    footer={formatRelativeTimeFromNow(project.lastInterviewAt).suffix ?? undefined}
                  />
                ),
              },
              {
                id: 'participant-privacy',
                title: 'Participant privacy',
                description: 'Current study privacy settings with quick edit access.',
                layout: {
                  x: 2,
                  y: 6,
                  w: 2,
                  h: 1,
                  minW: 1,
                  minH: 1,
                  maxW: 4,
                  maxH: 2,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectParticipantPrivacyPanel
                    project={project}
                    panelId="participant-privacy"
                    currentSpan={panelSpanFromLayout(layout, '2x1')}
                    onEdit={() => setEditParticipantPrivacyOpen(true)}
                  />
                ),
              },
              {
                id: 'study-access',
                title: 'Study access',
                description: 'Users with explicit access to this study.',
                layout: {
                  x: 2,
                  y: 7,
                  w: 2,
                  h: 2,
                  minW: 1,
                  minH: 1,
                  maxW: 4,
                  maxH: 4,
                  isResizable: true,
                },
                render: (layout) => (
                  <ProjectAccessPanel
                    accessUsers={project.accessUsers}
                    panelId="study-access"
                    currentSpan={panelSpanFromLayout(layout, '2x2')}
                  />
                ),
              },
              ...createTargetScopeTileItems(9),
              createProjectTagsTileItem(project, 23),
            ]}
          />
        ) : null}

        {project && changeProductLineOpen ? (
          <ChangeProjectProductLineModal
            project={project}
            onClose={() => setChangeProductLineOpen(false)}
            onUpdated={(updatedProject) => {
              setProject(updatedProject);
              void queryClient.invalidateQueries({
                queryKey: ['backoffice-interview-link', updatedProject.id],
              });
            }}
          />
        ) : null}
        {project && changeOwnershipOpen ? (
          <ChangeProjectOwnershipModal
            project={project}
            onClose={() => setChangeOwnershipOpen(false)}
            onUpdated={(updatedProject) => {
              setProject(updatedProject);
              setChangeOwnershipOpen(false);
            }}
          />
        ) : null}
        {project && editParticipantPrivacyOpen ? (
          <EditProjectParticipantPrivacyModal
            project={project}
            onClose={() => setEditParticipantPrivacyOpen(false)}
            onUpdated={(updatedProject) => {
              setProject(updatedProject);
              setEditParticipantPrivacyOpen(false);
            }}
          />
        ) : null}
      </div>
    </main>
  );
}
